import React, {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Sidebar from '../../components/Sidebar/Sidebar';
import CreateProjectPopup from '../../components/CreateProjectPopup/CreateProjectPopup';
import CreateTemplatePopup from '../../components/CreateTemplatePopup/CreateTemplatePopup';
import ConfirmationDialog from '../../components/ui/ConfirmationDialog/ConfirmationDialog';
import NotificationBell from '../../components/NotificationBell/NotificationBell';
import SystemAdminBadge from '../../components/SystemAdminBadge/SystemAdminBadge';
import LanguageSwitcher from '../../components/LanguageSwitcher/LanguageSwitcher';
import {useCurrentUserPermissions} from '../../hooks/useCurrentUserPermissions';
import {useGetTemplate, useDeleteTemplateId} from '../../api/template-api/template-api';
import {PermissionKey} from '../../api/model';
import {useQueryClient} from '@tanstack/react-query';
import {useToast} from '../../components/ui/Toast/ToastProvider';
import {IconSearch, IconTemplate, IconPlus, IconTrash, IconFolderPlus} from '@tabler/icons-react';
import styles from './TemplatesPage.module.scss';
import type {TemplateDTO} from '../../api/model';

const TemplatesPage: React.FC = () => {
    const {t} = useTranslation('templates');
    const queryClient = useQueryClient();
    const {showSuccess, showError} = useToast();
    const {can, isSystemAdmin} = useCurrentUserPermissions();
    const canSaveTemplates = !isSystemAdmin && can(PermissionKey.SAVE_TEMPLATES, 'CREATE');
    const canCreateProjects = !isSystemAdmin && can(PermissionKey.CREATE_PROJECT, 'CREATE');

    const {data: templates = [], isLoading} = useGetTemplate({
        query: {
            staleTime: 5 * 60 * 1000,
            retry: false,
        },
    });

    const deleteMutation = useDeleteTemplateId({
        mutation: {
            onSuccess: () => {
                showSuccess(
                    t('templates.delete.successTitle', 'Template deleted'),
                    t('templates.delete.successMessage', 'The template has been removed.')
                );
                queryClient.invalidateQueries({queryKey: ['/template']});
                setTemplateToDelete(null);
            },
            onError: (error: any) => {
                showError(
                    t('templates.delete.errorTitle', 'Template delete failed'),
                    error?.message || t('templates.delete.errorMessage', 'Could not delete the template.')
                );
            },
        },
    }, queryClient);

    const [search, setSearch] = useState('');
    const [isCreateTemplateOpen, setIsCreateTemplateOpen] = useState(false);
    const [templateToInstantiate, setTemplateToInstantiate] = useState<TemplateDTO | null>(null);
    const [templateToDelete, setTemplateToDelete] = useState<TemplateDTO | null>(null);

    const filteredTemplates = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return templates;

        return templates.filter((template) => {
            const haystack = [
                template.name,
                template.description,
                template.projectType,
                template.method,
                template.complexity,
                template.sprintFrequency,
                String(template.sourceProjectId ?? ''),
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();

            return haystack.includes(term);
        });
    }, [search, templates]);

    const handleDeleteConfirm = () => {
        if (!templateToDelete?.id) return;
        deleteMutation.mutate({id: templateToDelete.id});
    };

  return (
    <div className={styles.pageLayout}>
      <Sidebar />
      <div className={styles.pageContent}>
          <div className={styles.pageHeader}>
              <div>
                  <h1 className={styles.pageTitle}>{t('templates.title', 'Templates')}</h1>
                  <p className={styles.pageDescription}>
                      {t('templates.description', 'Manage reusable project templates and create new projects from them.')}
                  </p>
              </div>
              <div className={styles.headerActions}>
                  <LanguageSwitcher />
                  {isSystemAdmin ? <SystemAdminBadge /> : <NotificationBell />}
                  <div className={styles.searchField}>
                      <IconSearch size={16}/>
                      <input
                          type="search"
                          value={search}
                          onChange={(event) => setSearch(event.target.value)}
                          placeholder={t('templates.searchPlaceholder', 'Search templates')}
                      />
                  </div>
                  {canSaveTemplates && (
                      <button className={styles.secondaryButton} onClick={() => setIsCreateTemplateOpen(true)}>
                          <IconPlus size={16}/>
                          <span>{t('templates.actions.saveProject', 'Save project as template')}</span>
                      </button>
                  )}
              </div>
          </div>

          {isSystemAdmin && (
              <div className={styles.infoBanner}>
                  {t('templates.systemAdminBlocked', 'System admin accounts are not associated with a tenant and cannot create or manage templates.')}
              </div>
          )}

          <div className={styles.templateStats}>
              <div className={styles.statTile}>
                  <span className={styles.statLabel}>{t('templates.stats.total', 'Templates')}</span>
                  <span className={styles.statValue}>{templates.length}</span>
              </div>
              <div className={styles.statTile}>
                  <span className={styles.statLabel}>{t('templates.stats.filtered', 'Filtered')}</span>
                  <span className={styles.statValue}>{filteredTemplates.length}</span>
              </div>
              <div className={styles.statTile}>
                  <span className={styles.statLabel}>{t('templates.stats.active', 'Available')}</span>
                  <span
                      className={styles.statValue}>{canCreateProjects ? t('templates.stats.yes', 'Yes') : t('templates.stats.no', 'No')}</span>
              </div>
          </div>

          {isLoading ? (
              <div className={styles.stateBlock}>{t('templates.loading', 'Loading templates…')}</div>
          ) : filteredTemplates.length === 0 ? (
              <div className={styles.emptyState}>
                  <IconTemplate size={28}/>
                  <h2>{search ? t('templates.empty.filteredTitle', 'No templates match your search') : t('templates.empty.title', 'No templates yet')}</h2>
                  <p>
                      {search
                          ? t('templates.empty.filteredText', 'Try a different search term.')
                          : t('templates.empty.text', 'Save a project as a template to reuse its structure later.')}
                  </p>
                  {canSaveTemplates && !search && (
                      <button className={styles.primaryButton} onClick={() => setIsCreateTemplateOpen(true)}>
                          <IconFolderPlus size={16}/>
                          <span>{t('templates.actions.saveProject', 'Save project as template')}</span>
                      </button>
                  )}
              </div>
          ) : (
              <div className={styles.templateGrid}>
                  {filteredTemplates.map((template) => {
                      const phaseCount = template.content?.phases?.length ?? 0;
                      const characterCount = template.content?.projectCharacters?.length ?? 0;

                      return (
                          <article key={template.id ?? template.name} className={styles.templateCard}>
                              <div className={styles.templateCardHeader}>
                                  <div className={styles.templateIcon}>
                                      <IconTemplate size={20}/>
                                  </div>
                                  <div className={styles.templateMeta}>
                                      <h3 className={styles.templateName}>{template.name || t('templates.fallbackName', 'Untitled template')}</h3>
                                      <span className={styles.templateSource}>
                        {template.sourceProjectId
                            ? t('templates.sourceProject', {
                                id: template.sourceProjectId,
                                defaultValue: `Source project #${template.sourceProjectId}`
                            })
                            : t('templates.sourceProjectMissing', 'Source project not set')}
                      </span>
                                  </div>
                                  <div className={styles.cardButtons}>
                                      {canCreateProjects && template.id != null && (
                                          <button
                                              className={styles.iconButton}
                                              onClick={() => setTemplateToInstantiate(template)}
                                              title={t('templates.actions.createProject', 'Create project from template')}
                                          >
                                              <IconFolderPlus size={16}/>
                                          </button>
                                      )}
                                      {canSaveTemplates && template.id != null && (
                                          <button
                                              className={styles.iconButton}
                                              onClick={() => setTemplateToDelete(template)}
                                              title={t('templates.actions.deleteTemplate', 'Delete template')}
                                          >
                                              <IconTrash size={16}/>
                                          </button>
                                      )}
                                  </div>
                              </div>

                              {template.description && (
                                  <p className={styles.templateDescription}>{template.description}</p>
                              )}

                              <div className={styles.templateDetails}>
                                  <div>
                                      <span
                                          className={styles.detailLabel}>{t('templates.details.method', 'Method')}</span>
                                      <span className={styles.detailValue}>{template.method || '—'}</span>
                                  </div>
                                  <div>
                                      <span
                                          className={styles.detailLabel}>{t('templates.details.complexity', 'Complexity')}</span>
                                      <span className={styles.detailValue}>{template.complexity || '—'}</span>
                                  </div>
                                  <div>
                                      <span
                                          className={styles.detailLabel}>{t('templates.details.sprint', 'Sprint')}</span>
                                      <span className={styles.detailValue}>{template.sprintFrequency || '—'}</span>
                                  </div>
                                  <div>
                                      <span
                                          className={styles.detailLabel}>{t('templates.details.phases', 'Phases')}</span>
                                      <span className={styles.detailValue}>{phaseCount}</span>
                                  </div>
                                  <div>
                                      <span
                                          className={styles.detailLabel}>{t('templates.details.characters', 'Characters')}</span>
                                      <span className={styles.detailValue}>{characterCount}</span>
                                  </div>
                              </div>

                              <div className={styles.templateCardFooter}>
                                  {canCreateProjects && template.id != null && (
                                      <button className={styles.primaryButton}
                                              onClick={() => setTemplateToInstantiate(template)}>
                                          <IconFolderPlus size={16}/>
                                          <span>{t('templates.actions.createProject', 'Create project from template')}</span>
                                      </button>
                                  )}
                              </div>
                          </article>
                      );
                  })}
              </div>
          )}
      </div>

        {isCreateTemplateOpen && (
            <CreateTemplatePopup
                onClose={() => setIsCreateTemplateOpen(false)}
                onSuccess={() => {
                    queryClient.invalidateQueries({queryKey: ['/template']});
                    setIsCreateTemplateOpen(false);
                }}
            />
        )}

        {templateToInstantiate?.id != null && (
            <CreateProjectPopup
                initialTemplateId={templateToInstantiate.id}
                onClose={() => setTemplateToInstantiate(null)}
                onSuccess={() => setTemplateToInstantiate(null)}
            />
        )}

        <ConfirmationDialog
            isOpen={templateToDelete != null}
            onClose={() => setTemplateToDelete(null)}
            onConfirm={handleDeleteConfirm}
            title={t('templates.delete.title', 'Delete template?')}
            message={t('templates.delete.message', {
                name: templateToDelete?.name || t('templates.fallbackName', 'this template'),
                defaultValue: `Remove "${templateToDelete?.name || 'this template'}" from the template library?`,
            })}
            confirmText={t('templates.delete.confirm', 'Delete')}
            cancelText={t('templates.delete.cancel', 'Cancel')}
            type="danger"
        />
    </div>
  );
};

export default TemplatesPage;
